import WriteButton from "@/components/WriteButton";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { getActiveStore } from "@/lib/scope";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import ReportActions from '@/components/ReportActions';
import DraftExcelTools from '@/components/DraftExcelTools';
import OpnameSelection from '@/components/OpnameSelection';
import StockMovementTab from '@/components/StockMovementTab';
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Search, Trash2, ClipboardCheck, Store, Activity, Boxes, ArrowDownToLine, ArrowUpFromLine, PackagePlus } from "lucide-react";
import { useListNav } from "@/lib/useListNav";
import { useSupervisorApproval } from "@/components/SupervisorApprovalDialog";

export default function Stok() {
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = ["stok", "masuk", "keluar", "opname", "pergerakan"];
  const tab = validTabs.includes(searchParams.get("tab")) ? searchParams.get("tab") : "stok";

  const load = async () => {
    try {
      const [pr, st, cats] = await Promise.all([
        api.get("/products"), api.get("/stores"), api.get("/categories"),
      ]);
      setProducts(pr.data); setStores(st.data); setCategories(cats.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const activeName = stores.find(store => store.id === getActiveStore())?.name || stores[0]?.name || "—";

  return (
    <>
      <PageHeader title="Inventori" subtitle="Stok saat ini, penerimaan, pengeluaran, opname, dan seluruh pergerakan stok">
        <div className="flex items-center gap-2 rounded-xl bg-ui-card border border-ui-line px-3 py-2" data-testid="active-store-badge">
          <Store className="h-4 w-4 text-brand-readable" />
          <span className="text-xs text-ui-muted">Toko:</span>
          <span className="text-sm font-semibold text-ui-text">{activeName}</span>
        </div>
      </PageHeader>

      <div className="flex-1 min-w-0 overflow-y-auto p-3 sm:p-5">
        <Tabs value={tab} onValueChange={value => setSearchParams({ tab: value })}>
          <div className="overflow-x-auto pb-1">
          <TabsList className="bg-ui-card border border-ui-line h-auto min-w-max">
            <TabsTrigger value="stok" data-testid="tab-stok" className="gap-1"><Boxes className="h-4 w-4" /> Stok Saat Ini</TabsTrigger>
            <TabsTrigger value="masuk" data-testid="tab-masuk" className="gap-1"><ArrowDownToLine className="h-4 w-4" /> Stok Masuk</TabsTrigger>
            <TabsTrigger value="keluar" data-testid="tab-keluar" className="gap-1"><ArrowUpFromLine className="h-4 w-4" /> Stok Keluar</TabsTrigger>
            <TabsTrigger value="opname" data-testid="tab-opname" className="gap-1"><ClipboardCheck className="h-4 w-4" /> Opname</TabsTrigger>
            <TabsTrigger value="pergerakan" data-testid="tab-pergerakan" className="gap-1"><Activity className="h-4 w-4" /> Pergerakan</TabsTrigger>
          </TabsList>
          </div>

          <TabsContent value="stok" className="mt-5">
            <CurrentStockTab products={products} />
          </TabsContent>
          <TabsContent value="masuk" className="mt-5">
            <StockMovementTab type="masuk" />
          </TabsContent>
          <TabsContent value="keluar" className="mt-5">
            <StockMovementTab type="keluar" />
          </TabsContent>
          <TabsContent value="opname" className="mt-5">
            <OpnameTab products={products} categories={categories} activeName={activeName} onDone={load} />
          </TabsContent>
          <TabsContent value="pergerakan" className="mt-5">
            <StockMovementTab />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

function CurrentStockTab({ products }) {
  const [search, setSearch] = useState("");
  const rows = useMemo(() => products.filter(product => {
    const query = search.trim().toLowerCase();
    return !query || product.name.toLowerCase().includes(query) || (product.barcode || "").toLowerCase().includes(query);
  }), [products, search]);
  return <div className="space-y-3">
    <ReportActions title="Stok Saat Ini" headers={['Barang','Barcode','Satuan Dasar','Stok Tersedia','Stok Fisik Sistem','Dicadangkan','Batas Minimum']} rows={rows.map(p => [p.name,p.barcode,p.base_unit,p.stock,p.stock_physical ?? p.stock,p.stock_reserved ?? 0,p.min_stock])} notes={['Stok tersedia sudah memperhitungkan cadangan transfer. Data merupakan snapshot saat ekspor.']} />
    <div className="relative max-w-xl">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ui-muted" />
      <Input data-testid="current-stock-search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Cari nama atau barcode produk…" className="pl-10 bg-ui-card border-ui-line" />
    </div>
    <div className="rounded-xl border border-ui-line overflow-x-auto" data-testid="current-stock-table">
      <table className="w-full min-w-[620px] text-sm">
        <thead className="bg-ui-card text-ui-muted text-xs uppercase"><tr><th className="text-left p-3">Produk</th><th className="text-left p-3">Barcode</th><th className="text-right p-3">Stok</th><th className="text-right p-3">Batas Minimum</th><th className="text-left p-3">Status</th></tr></thead>
        <tbody>{rows.map(product => {
          const stock = Number(product.stock || 0); const minimum = Number(product.min_stock ?? 5);
          const status = stock <= 0 ? "Kosong" : stock <= minimum ? "Menipis" : "Aman";
          const tone = stock <= 0 ? "text-danger-readable bg-danger-soft" : stock <= minimum ? "text-warning-readable bg-warning-soft" : "text-brand-readable bg-brand-soft";
          return <tr key={product.id} className="border-t border-ui-line"><td className="p-3 font-medium">{product.name}</td><td className="p-3 font-mono text-ui-muted">{product.barcode || "-"}</td><td className="p-3 text-right font-mono font-bold">{stock.toLocaleString("id-ID")}</td><td className="p-3 text-right font-mono text-ui-muted">{minimum.toLocaleString("id-ID")}</td><td className="p-3"><span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${tone}`}>{status}</span></td></tr>;
        })}{rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-ui-muted">Produk tidak ditemukan</td></tr>}</tbody>
      </table>
    </div>
  </div>;
}

function ProductPicker({ products, exclude, onPick, testid }) {
  const [search, setSearch] = useState("");
  const filtered = useMemo(
    () => (search ? products.filter((p) => (p.name.toLowerCase().includes(search.toLowerCase()) || (p.barcode || "").includes(search)) && !exclude.includes(p.id)).slice(0, 6) : []),
    [search, products, exclude]
  );
  const nav = useListNav(filtered, (p) => { onPick(p); setSearch(""); });
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ui-muted" />
      <Input data-testid={testid} value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={nav.onInputKeyDown} placeholder="Cari & tambahkan barang…" className="pl-10 bg-ui-card border-ui-line" />
      {filtered.length > 0 && (
        <div ref={nav.listRef} className="absolute z-10 mt-1 w-full rounded-lg border border-ui-line bg-ui-card shadow-xl overflow-hidden">
          {filtered.map((p, idx) => (
            <button key={p.id} data-testid={`pick-${p.id}`} data-nav-index={idx} onMouseEnter={() => nav.setActive(idx)} onClick={() => { onPick(p); setSearch(""); }} className={`w-full text-left px-3 py-2 flex items-center justify-between ${idx === nav.active ? "bg-ui-muted-surface" : "hover:bg-ui-muted-surface"}`}>
              <span className="text-sm">{p.name}</span>
              <span className="text-xs text-ui-muted font-mono">stok {p.stock}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function OpnameTab({ products, categories, activeName, onDone }) {
  const { approveAction, approvalDialog } = useSupervisorApproval();
  const [lines, setLines] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const addLine = (p) => setLines((prev) => (prev.find((l) => l.product_id === p.id) ? prev : [...prev, { product_id: p.id, name: p.name, before: p.stock, counted: p.stock, cost: p.cost || 0 }]));
  const setCounted = (i, v) => setLines((prev) => prev.map((l, x) => (x === i ? { ...l, counted: v } : l)));
  const removeLine = (i) => setLines((prev) => prev.filter((_, x) => x !== i));

  const totals = useMemo(() => lines.reduce((a, l) => {
    const d = Number(l.counted || 0) - Number(l.before || 0);
    return { qty: a.qty + d, value: a.value + d * Number(l.cost || 0) };
  }, { qty: 0, value: 0 }), [lines]);

  const submit = async () => {
    if (lines.length === 0) return toast.error("Tambahkan minimal 1 barang");
    let approval;
    try { approval = await approveAction("stock_correction", `Opname ${lines.length} barang di ${activeName} · selisih ${totals.qty} pcs`); }
    catch (error) { return toast.error(apiError(error)); }
    if (approval === false) return;
    setSaving(true);
    try {
      const { data } = await api.post("/opname", { items: lines.map((l) => ({ product_id: l.product_id, counted: Number(l.counted || 0) })), note: note || null }, approval);
      toast.success(`Opname ${data.opname_no} tersimpan · selisih ${data.total_diff_qty} pcs (${rupiah(data.total_diff_value)})`);
      setLines([]); setNote(""); onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <>
    {approvalDialog}
    <div className="space-y-4">
      <div className="text-sm text-ui-muted">Sesuaikan stok fisik untuk toko <b className="text-ui-text">{activeName}</b>. Sistem akan mengganti stok = jumlah fisik dan mencatat selisih.</div>
      <OpnameSelection products={products} categories={categories} activeName={activeName} disabled={saving} onAdd={picked => setLines(previous => {
        const existing = new Set(previous.map(l => l.product_id));
        return [...previous, ...picked.filter(p => !existing.has(p.id)).map(p => ({ product_id: p.id, name: p.name, before: p.stock, counted: p.stock, cost: p.cost || 0 }))];
      })} />
      <ProductPicker products={products} exclude={lines.map((l) => l.product_id)} onPick={addLine} testid="opname-search" />
      <DraftExcelTools kind="opname" existingIds={lines.map(l => l.product_id)} disabled={saving} onAppend={rows => {
        const imported = rows.map(row => {
          const p = products.find(product => product.id === row.product_id);
          if (!p || row.pieces !== 1 || row.unit.toLowerCase() !== (p.base_unit || 'Pcs').toLowerCase()) throw new Error(`Opname ${row.name}: gunakan satuan dasar produk, bukan kemasan.`);
          return { product_id:p.id, name:p.name, before:p.stock, counted:row.qty, cost:p.cost || 0 };
        });
        setLines(previous => [...previous, ...imported]);
      }} />
      <div className="rounded-xl border border-ui-line overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="bg-ui-card text-ui-muted text-xs uppercase">
            <tr>
              <th className="text-left p-2 pl-3">Barang</th>
              <th className="text-center p-2 w-24">Stok Sistem</th>
              <th className="text-center p-2 w-28">Stok Fisik</th>
              <th className="text-right p-2 w-28">Selisih</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody data-testid="opname-lines">
            {lines.map((l, i) => {
              const diff = Number(l.counted || 0) - Number(l.before || 0);
              return (
                <tr key={i} className="border-t border-ui-line">
                  <td className="p-2 pl-3">{l.name}</td>
                  <td className="p-2 text-center font-mono text-ui-muted">{l.before}</td>
                  <td className="p-2"><Input data-testid={`opname-counted-${i}`} type="number" value={l.counted} onChange={(e) => setCounted(i, e.target.value)} className="h-9 text-center font-mono bg-ui-soft border-ui-line" /></td>
                  <td className={`p-2 text-right font-mono font-bold ${diff > 0 ? "text-brand-readable" : diff < 0 ? "text-danger-readable" : "text-ui-muted"}`}>{diff > 0 ? `+${diff}` : diff}</td>
                  <td className="p-2 text-center"><button data-testid={`opname-remove-${i}`} onClick={() => removeLine(i)} className="text-danger-readable hover:text-danger-readable"><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              );
            })}
            {lines.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-ui-muted"><PackagePlus className="h-6 w-6 mx-auto mb-1" /> Belum ada barang</td></tr>}
          </tbody>
        </table>
      </div>
      <Textarea data-testid="opname-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan opname (opsional)" className="bg-ui-card border-ui-line min-h-[38px]" rows={1} />
      <div className="flex flex-wrap gap-3 items-center justify-between rounded-xl bg-ui-card border border-ui-line px-4 py-3">
        <span className="text-ui-muted text-sm">Total selisih: <b data-testid="opname-total-qty" className="text-ui-text">{totals.qty} pcs</b> · <span data-testid="opname-total-value" className={totals.value < 0 ? "text-danger-readable" : "text-brand-readable"}>{rupiah(totals.value)}</span></span>
        <WriteButton data-testid="save-opname-btn" onClick={submit} disabled={saving} className="bg-brand-button hover:bg-brand-button text-ui-text font-bold">Simpan Opname</WriteButton>
      </div>
    </div>
    </>
  );
}
